/* eslint no-use-before-define: 0 */

import $ from "jquery";
import Rx from "rxjs/Rx";

/**
 * Public API:
 * - event areaValueUpdated$
 * - get areaValue
 */

import { AreaFiltersRowComponent, areaRowSelectionUpdated$ } from "./area-filters-row.component";
import { CalculatorComponent, calculatorValueUpdated$ } from "./calculator.component";
import { searchTypeFilterEnabled$ } from "./search-page-type-filter.component";
import { allSearchTypeFiltersDisabled$ } from "./type-filters-panel.component";
import { calculatorTabSelected$, rowsTabSelected$ } from "./tab-switcher.component";

const states = {
    nothingSelected: 0,
    calculatorSelected: 1,
    areaRowSelected: 2,
};

const areaValueUpdatedSource = new Rx.Subject();
/**
 * no args.
 */
export const areaValueUpdated$ = areaValueUpdatedSource.asObservable();

export class AreaFiltersPanelComponent {
    /**
     * @param {Object} node - JQuery node. Unique for every AreaFiltersPanelComponent class instance.
     * @param {Object} parentNode - JQuery node of parent(SearchPanel).
     * Unique for every AreaFiltersPanelComponent class instance.
     */
    constructor(node, parentNode) {
        this.node = node;
        this.parentNode = parentNode;
        this.areaFilterRows = {
            instances: [],
            hideAll: () => { },
            showCurrent: () => { },
            current: null,
        };
        this.calculator = {
            instance: null,
            show: () => { },
            hide: () => { },
        };
        this.searchPanelSwitcher = null;
        this.currentTypeFilter = null;
        this.currentState = states.nothingSelected;
        this.initialize();
        this.subscribeOnCalculatorTabSelected();
        this.subscribeOnRowsTabSelected();
    }

    initialize() {
        const scope = this;

        if (this.node) {
            initializeInNormalMode();
        } else {
            initializeInCompatibilyMode();
        }

        function initializeInNormalMode() {
            scope.initializeChildComponents();
            scope.initializeEventHandlers();
        }

        /**
         * we need it because search service depennds on this class.
         */
        function initializeInCompatibilyMode() {
            searchTypeFilterEnabled$.subscribe(() => {
                areaValueUpdatedSource.next();
            });

            allSearchTypeFiltersDisabled$.subscribe(() => {
                areaValueUpdatedSource.next();
            });
        }
    }

    subscribeOnCalculatorTabSelected() {
        calculatorTabSelected$.subscribe(() => {
            this.areaFilterRows.hideAll();
            setPassiveVisualState();
            this.currentState = states.calculatorSelected;
            areaValueUpdatedSource.next();
        });

        const scope = this;
        function setPassiveVisualState() {
            scope.node.removeClass("active");
        }
    }

    subscribeOnRowsTabSelected() {
        rowsTabSelected$.subscribe(() => {
            this.areaFilterRows.hideAll();
            this.areaFilterRows.showCurrent();
            setActiveVisualState();
            this.currentState = states.areaRowSelected;
            areaValueUpdatedSource.next();
        });

        const scope = this;
        function setActiveVisualState() {
            scope.node.addClass("active");
        }
    }

    /**
     * @returns {Object} - return {min,max} or null, based on current state.
     */
    get areaValue() {
        switch (this.currentState) {
            case states.nothingSelected:
                return null;
            case states.calculatorSelected:
                if (!this.calculator.instance.calculatorValue) {
                    return null;
                }

                return {
                    min: this.calculator.instance.calculatorValue.min,
                    max: this.calculator.instance.calculatorValue.max,
                };
            case states.areaRowSelected:
                if (!this.areaFilterRows.current || !this.areaFilterRows.current.selectedAreaFilter) {
                    return null;
                }

                return {
                    min: this.areaFilterRows.current.selectedAreaFilter.min,
                    max: this.areaFilterRows.current.selectedAreaFilter.max,
                };

            default: {
                throw new Error("unxpected behaviour with currentState of AreaPanel");
            }
        }
    }

    initializeChildComponents() {
        const scope = this;

        initializeAreaFilterRows();
        initializeCalculator();

        function initializeAreaFilterRows() {
            scope.node.find(".custom-area-filters-row-component").each(function createAreaFilterRow() {
                scope.areaFilterRows.instances.push(new AreaFiltersRowComponent($(this)));
            });

            scope.areaFilterRows.hideAll = () => {
                scope.areaFilterRows.instances.forEach((row) => {
                    row.setPassiveVisualState();
                });
            };

            scope.areaFilterRows.showCurrent = () => {
                if (scope.areaFilterRows.current) {
                    scope.areaFilterRows.current.setActiveVisualState();
                }
            };
        }

        function initializeCalculator() {
            scope.calculator.instance = new CalculatorComponent(
                scope.parentNode.find(".office-floor-space-calculator").eq(0),
            );

            scope.calculator.show = () => {
                scope.calculator.instance.setActiveVisualState();
            };

            scope.calculator.hide = () => {
                scope.calculator.instance.setPassiveVisualState();
            };
        }
    }

    initializeEventHandlers() {
        const scope = this;

        subscribeOnCalculatorValueUpdated$();
        subscribeOnAreaRowSelectionUpdated$();
        subscribeOnTypeFiltersChanges$();

        function subscribeOnCalculatorValueUpdated$() {
            calculatorValueUpdated$.subscribe(() => {
                areaValueUpdatedSource.next();
            });
        }

        function subscribeOnAreaRowSelectionUpdated$() {
            areaRowSelectionUpdated$.subscribe(() => {
                areaValueUpdatedSource.next();
            });
        }

        function subscribeOnTypeFiltersChanges$() {
            searchTypeFilterEnabled$.subscribe((typeFilter) => {
                scope.currentTypeFilter = typeFilter;
                scope.areaFilterRows.current = scope.areaFilterRows.instances[typeFilter.index];
            });

            allSearchTypeFiltersDisabled$.subscribe(() => {
                scope.currentTypeFilter = null;
                scope.currentState = states.nothingSelected;
                scope.areaFilterRows.current = null;
                scope.areaFilterRows.hideAll();
                areaValueUpdatedSource.next();
            });
        }
    }
}
